/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import BaseLogic from "../../utils/BaseLogic";
import LocalizedPolicyLogic from "./LocalizedPolicyLogic";
import PoliciesLogic, { PolicyType } from "./PoliciesLogic";
import { LocalizedPolicyInformation, LocalizedPolicy, PolicyList } from "../../api/legal/models/policies";
import { InvalidLocalizedPolicyData } from "../../api/legal/models/legal";
import { uiEvents } from "../../app-message/events/uiEvents";

interface Props {
  localizedPolicyId?: string;
}
interface State {
  policies: PolicyList | null;
  policiesError: Error | null;
  isFetchingPolicies: boolean;

  localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation | null;
  localizedPolicyError: Error | null;
  isFilterMenuShown?: boolean;
}

export default class DocumentPageLogic extends BaseLogic<State> {
  private readonly localizedPolicyLogic: LocalizedPolicyLogic | null;
  private readonly policiesLogic: PoliciesLogic;
  private firstLoad = true;
  constructor(props: Props) {
    super();
    const { localizedPolicyId } = props;
    if (localizedPolicyId) {
      this.localizedPolicyLogic = new LocalizedPolicyLogic({ localizedPolicyId });
      this.localizedPolicyLogic.subscribe(this.onLocalizedPolicyLogicStateChange);
    } else {
      this.firstLoad = false;
    }
    this.policiesLogic = new PoliciesLogic();
    this.policiesLogic.subscribe(this.onPoliciesLogicStateChange);
    uiEvents.subscribe("onNavigationDrawerMenuClick", this.onNavigationDrawerMenuClick)
    this.state = {
      policies: null,
      isFetchingPolicies: false,
      policiesError: null,
      localizedPolicy: null,
      localizedPolicyError: null,
      isFilterMenuShown: false
    };
  }

  onNavigationDrawerMenuClick = (isFilterMenuShown: boolean): void => {
    this.setState({
      isFilterMenuShown
    })
  };

  onLocalizedPolicyLogicStateChange = () => {
    if (!this.localizedPolicyLogic) return;
    const { localizedPolicyData, isLoading, error } = this.localizedPolicyLogic.state;
    this.setState({
      localizedPolicy: localizedPolicyData,
      isFetchingPolicies: isLoading,
      policiesError: error,
    });
  };

  onPoliciesLogicStateChange = async () => {
    if (!this.policiesLogic) return;
    const { policies, isLoading, error } = this.policiesLogic.state;
    await this.setState({
      policies,
      isFetchingPolicies: isLoading,
      policiesError: error,
    });
    this.setLocalizedPolicyData();
  };

  setLocalizedPolicyData() {
    if (this.firstLoad) return;
    try {
      const localizedPolicy = this.getFirstLocalizedPolicyData();
      if (!localizedPolicy) throw new InvalidLocalizedPolicyData();
      this.setState({ localizedPolicy });
    } catch (error) {
      this.setState({ policiesError: error });
    }
  }

  getFirstLocalizedPolicyData(): LocalizedPolicy | LocalizedPolicyInformation | undefined {
    const { policies } = this.state;
    if (!Array.isArray(policies) || !policies.length) return;
    if (!Array.isArray(policies[0].policyVersions) || !policies[0].policyVersions.length) return;
    if (
      !Array.isArray(policies[0].policyVersions[0].localizedPolicyVersions) ||
      !policies[0].policyVersions[0].localizedPolicyVersions.length
    )
      return;
    return policies[0].policyVersions[0].localizedPolicyVersions[0];
  }

  async init() {
    const { localizedPolicyLogic, policiesLogic } = this;
    await this.setState({ isFetchingPolicies: true });
    try {
      if (!!localizedPolicyLogic) {
        // If page is called with localizedPolicyId provided
        await localizedPolicyLogic.init();

        const { localizedPolicyData, error } = localizedPolicyLogic.state;
        if (error) throw error;

        // If policies error is empty but no localizedPolicyData
        if (!localizedPolicyData) throw new Error();

        // If policy version is not inEffet that means this localized policy is an out of date document so doesn't need to fetch policies information
        if (!!localizedPolicyData.policyVersion && !localizedPolicyData.policyVersion.isInEffect) return;

        if (localizedPolicyData.policy) {
          // If localizedPolicyData has policy and country code then fetch policies information
          await policiesLogic.init(localizedPolicyData.policy.countryCode, PolicyType.LEGAL_DOCUMENT_TYPE);
        }

        // await this.setState({ localizedPolicy: localizedPolicyData });
      } else {
        // If page is called with no localizedPolicyId provided
        await policiesLogic.init(undefined, PolicyType.LEGAL_DOCUMENT_TYPE);
      }
    } catch (error) {
      await this.setState({ policiesError: error });
    } finally {
      await this.setState({ isFetchingPolicies: false });
    }
  }

  async changeCountry(countryCode: string) {
    await this.setState({
      policiesError: null,
      localizedPolicy: null,
      localizedPolicyError: null,
      isFetchingPolicies: true,
    });
    this.firstLoad = false;
    await this.policiesLogic.fetchPoliciesByCountry(countryCode);
  }

  changeActivePolicy = async (localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation) => {
    uiEvents.onNavigationDrawerMenuClick(false);
    await this.setState({ localizedPolicy: localizedPolicy });
  };
}
